import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SearchBox } from "../SearchBox";
import { MEDIA } from "../../constants/media";
import { ShieldCheck, Sparkles, Clock, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const QUICK = ["Restauração", "Logística", "Retalho", "Limpeza"];
const SLIDE_INTERVAL_MS = 5500;

export const Hero = ({ stats }) => {
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();

  const slides = MEDIA.heroSlides;
  const total = slides.length;

  const next = useCallback(() => setSlide((s) => (s + 1) % total), [total]);
  const prev = useCallback(() => setSlide((s) => (s - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused, next]);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute -top-40 -right-24 w-[38rem] h-[38rem] rounded-full bg-amber-100/50 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute top-72 -left-40 w-[30rem] h-[30rem] rounded-full bg-sky-100/40 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-16 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-black/5 bg-white px-3 py-1.5 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
              Todo o país · 308 concelhos · Açores e Madeira
            </span>
          </div>

          <h1 className="font-heading font-black tracking-tighter text-[2.6rem] sm:text-6xl lg:text-[4.2rem] text-slate-900 mt-6 leading-[0.98]">
            O teu próximo emprego
            <span className="block">começa <span className="relative inline-block">aqui
              <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none" aria-hidden="true">
                <path d="M2 9C40 3 120 2 198 6" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </span>.</span>
          </h1>

          <p className="text-slate-600 mt-7 max-w-xl text-[17px] leading-relaxed">
            Vagas reais em restauração, logística, retalho e mais. Candidata-te em minutos,
            acompanha cada candidatura em tempo real e recebe a análise do teu CV por inteligência artificial.
          </p>

          <SearchBox variant="hero" className="mt-9" />

          <p className="mt-3 text-xs text-slate-500">
            Escreve e vê sugestões de cargos, empresas e concelhos — como numa pesquisa.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 mr-1">Popular:</span>
            {QUICK.map((c) => (
              <button key={c} data-testid={`hero-quick-${c}`} onClick={() => navigate(`/empregos?category=${encodeURIComponent(c)}`)}
                className="text-xs font-medium text-slate-700 bg-white border border-black/5 rounded-full px-3 py-1.5 hover:border-amber-400 hover:text-amber-700 transition-colors">
                {c}
              </button>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-6 sm:gap-8">
            {[
              { icon: ShieldCheck, title: "100% grátis para candidatos", desc: "Sem subscrições escondidas" },
              { icon: Clock, title: "Candidatura em 2 minutos", desc: "Perfil e CV reutilizáveis" },
              { icon: Sparkles, title: "IA a teu favor", desc: "Análise de CV e match com a vaga" },
            ].map((t) => (
              <div key={t.title} className="flex items-start gap-3">
                <t.icon className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" strokeWidth={1.75} />
                <div>
                  <p className="text-sm font-semibold text-slate-900 leading-tight">{t.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="relative"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              data-testid="hero-carousel">

              {/* Carrossel */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-slate-300/60 border border-black/5 bg-slate-100">
                <div className="relative w-full h-[26rem] sm:h-[30rem]">
                  {slides.map((s, i) => (
                    <img key={s.src} src={s.src} alt={s.alt} loading={i === 0 ? "eager" : "lazy"}
                      data-testid={`hero-slide-${i}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        i === slide ? "opacity-100" : "opacity-0"
                      }`} />
                  ))}
                  {/* Gradiente para legibilidade das legendas */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-950/70 to-transparent pointer-events-none" aria-hidden="true" />

                  {/* Legenda / sector */}
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 text-white pointer-events-none">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-300">{slides[slide].sector}</p>
                      <p className="text-sm sm:text-base font-heading font-bold tracking-tighter mt-1 max-w-xs">{slides[slide].caption}</p>
                    </div>
                    <span className="text-[11px] font-mono text-white/80 shrink-0 tabular-nums">{String(slide + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
                  </div>

                  {/* Setas (desktop) */}
                  <button type="button" onClick={prev} aria-label="Slide anterior" data-testid="hero-prev"
                    className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white items-center justify-center shadow transition-colors">
                    <ChevronLeft className="w-4 h-4 text-slate-900" />
                  </button>
                  <button type="button" onClick={next} aria-label="Slide seguinte" data-testid="hero-next"
                    className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white items-center justify-center shadow transition-colors">
                    <ChevronRight className="w-4 h-4 text-slate-900" />
                  </button>
                </div>
              </div>

              {/* Dots */}
              <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label="Setores em destaque" data-testid="hero-dots">
                {slides.map((s, i) => (
                  <button key={s.src} type="button" role="tab" aria-selected={i === slide}
                    aria-label={`Mostrar setor ${s.sector}`}
                    onClick={() => setSlide(i)}
                    data-testid={`hero-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      i === slide ? "w-8 bg-slate-900" : "w-2 bg-slate-300 hover:bg-slate-500"
                    }`} />
                ))}
              </div>

              {/* Cartão flutuante: estado */}
              <div className="hidden sm:flex absolute -left-6 top-10 bg-white rounded-2xl border border-black/5 shadow-xl p-4 w-52 flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-600">Estado</span>
                <p className="text-sm font-semibold text-slate-900">Entrevista marcada</p>
                <p className="text-xs text-slate-500">Acompanhas cada passo da candidatura</p>
              </div>

              {/* Cartão flutuante: empresas */}
              <div className="hidden sm:flex absolute -right-4 bottom-24 bg-slate-900 rounded-2xl shadow-xl p-4 w-56 flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-sky-400">Empresas</span>
                <p className="text-sm font-semibold text-white">Triagem com IA</p>
                <p className="text-xs text-slate-400">Match score e resumo de cada candidato</p>
              </div>
            </div>

            {stats?.active_jobs > 0 && (
              <div className="mt-6 flex items-center justify-between bg-white rounded-2xl border border-black/5 px-5 py-4 shadow-sm">
                <div>
                  <p className="font-heading font-black tracking-tighter text-2xl text-slate-900">{stats.active_jobs}</p>
                  <p className="text-xs text-slate-500">vagas ativas agora</p>
                </div>
                <button data-testid="hero-see-jobs" onClick={() => navigate("/empregos")}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-amber-600 transition-colors">
                  Ver vagas <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
